import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { fieldClass, labelClass } from "../ui/formStyles";
import ComboBox from "../ui/ComboBox";
import { today } from "../../utils/dates";
import { SORT_SOONEST } from "./useDiscoverySearch";

/**
 * The discovery page's controls.
 *
 * Presentational: it owns no state. Every change is reported upwards so the
 * page can keep one copy of the filters and mirror it into the URL, which is
 * what makes a search shareable.
 */
const DiscoverFilters = ({ values, options, onChange, onReset, resultCount }) => {
  const { t } = useTranslation();

  const set = (key, value) => onChange({ ...values, [key]: value, page: 1 });

  const soonest = values.sort === SORT_SOONEST;

  const partsOfDay = [
    { value: "morning", label: t("Discover.morning") },
    { value: "afternoon", label: t("Discover.afternoon") },
    { value: "evening", label: t("Discover.evening") },
  ];

  // Categories first — "Coafor" is the broader choice — then the services
  // themselves, since a business that files everything under one category would
  // otherwise leave this list with a single entry.
  const serviceTypeGroups = [
    { label: t("Discover.categoriesGroup"), options: options.categories || [] },
    { label: t("Discover.servicesGroup"), options: options.services || [] },
  ].filter((group) => group.options.length > 0);

  const hasFilters = Boolean(
    values.q ||
      values.city ||
      values.category ||
      values.date ||
      values.partOfDay ||
      values.sort,
  );

  return (
    <div className="discover-filters rounded-card border border-ink-200 bg-white p-4 shadow-card sm:p-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="discover-q">
            {t("Discover.searchLabel")}
          </label>
          <input
            id="discover-q"
            type="search"
            className={fieldClass}
            placeholder={t("Discover.searchPlaceholder")}
            value={values.q || ""}
            onChange={(e) => set("q", e.target.value)}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="discover-city">
            {t("Discover.city")}
          </label>
          {/* A city no listed business is in — a customer's own city of
              residence, seeded from their profile — still shows as typed, so
              "Curtea de Arges, 0 results" reads true rather than "Any city"
              over an empty grid. */}
          <ComboBox
            id="discover-city"
            value={values.city || ""}
            onChange={(city) => set("city", city)}
            options={options.cities || []}
            anyLabel={t("Discover.anyCity")}
            placeholder={t("Discover.anyCity")}
            noMatchesLabel={t("Discover.noMatches")}
            clearLabel={t("Discover.clearChoice")}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="discover-category">
            {t("Discover.category")}
          </label>
          <ComboBox
            id="discover-category"
            value={values.category || ""}
            onChange={(category) => set("category", category)}
            groups={serviceTypeGroups}
            anyLabel={t("Discover.anyCategory")}
            placeholder={t("Discover.anyCategory")}
            noMatchesLabel={t("Discover.noMatches")}
            clearLabel={t("Discover.clearChoice")}
          />
        </div>
      </div>

      <div className="mt-4 border-t border-ink-100 pt-4">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <div>
            <label className={labelClass} htmlFor="discover-date">
              {t("Discover.date")}
            </label>
            <input
              id="discover-date"
              type="date"
              className={fieldClass}
              min={today()}
              value={values.date || ""}
              onChange={(e) => set("date", e.target.value)}
            />
          </div>

          {/* "When can I be seen soonest" is the question people actually
              come with; Today / Tomorrow / Weekend only guessed at it, and
              the date field already answers "on this day". */}
          <button
            type="button"
            aria-pressed={soonest}
            onClick={() => set("sort", soonest ? "" : SORT_SOONEST)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
              soonest
                ? "bg-brand-600 text-white shadow-brand"
                : "bg-ink-100 text-ink-600 hover:bg-ink-200"
            }`}
          >
            <i className="bi bi-lightning-charge" aria-hidden="true" />
            {t("Discover.soonestFirst")}
          </button>
        </div>

        {soonest ? (
          <p className="mt-2 text-fluid-sm text-ink-500">
            {values.date
              ? t("Discover.soonestHintDate")
              : t("Discover.soonestHint")}
          </p>
        ) : null}

        {/* Time of day only means something once there is a day or a "soonest"
            to narrow; the availability toggle only for a day, since "soonest"
            already leaves out whoever has nothing free. */}
        {values.date || soonest ? (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-3">
            <div className="flex flex-wrap gap-2">
              {partsOfDay.map((part) => {
                const active = values.partOfDay === part.value;
                return (
                  <button
                    key={part.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => set("partOfDay", active ? "" : part.value)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                      active
                        ? "border-brand-600 bg-brand-50 text-brand-700"
                        : "border-ink-200 text-ink-600 hover:border-ink-300"
                    }`}
                  >
                    {part.label}
                  </button>
                );
              })}
            </div>

            {values.date && !soonest ? (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-600">
                <input
                  type="checkbox"
                  className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-600/30"
                  checked={Boolean(values.availableOnly)}
                  onChange={(e) =>
                    onChange({
                      ...values,
                      availableOnly: e.target.checked,
                      page: 1,
                    })
                  }
                />
                {t("Discover.availableOnly")}
              </label>
            ) : null}
          </div>
        ) : null}
      </div>

      {hasFilters ? (
        <div className="mt-4 flex items-center justify-between border-t border-ink-100 pt-3">
          <p className="text-fluid-sm text-ink-500">
            {t("Discover.resultCount", { count: resultCount })}
          </p>
          <button
            type="button"
            onClick={onReset}
            className="text-sm font-semibold text-brand-700 transition hover:text-brand-800 hover:underline"
          >
            {t("Discover.clearFilters")}
          </button>
        </div>
      ) : null}
    </div>
  );
};

DiscoverFilters.propTypes = {
  values: PropTypes.object.isRequired,
  options: PropTypes.shape({
    cities: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.string),
    services: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  resultCount: PropTypes.number,
};

export default DiscoverFilters;
